import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import {
  TrendingUp,
  Users,
  Clock,
  Award,
  Download,
  RefreshCw,
  BookOpen,
  Video,
  FileText,
  Star,
  AlertTriangle,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Minus,
  Target,
} from "lucide-react";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

// Some elearning types are not exported in the workspace; use local any aliases
type LearningAnalytics = any;
type StudentEngagement = any;
type CourseProgress = any;
type AssessmentAnalytics = any;
type ContentAnalytics = any;
import apiClient from "../../services/api";
import { useParams } from "react-router-dom";
import quizService from "../../services/quizService";

interface AnalyticsState {
  overview: LearningAnalytics | null;
  studentEngagement: StudentEngagement[];
  courseProgress: CourseProgress[];
  assessmentAnalytics: AssessmentAnalytics | null;
  contentAnalytics: ContentAnalytics | null;
  loading: boolean;
  error: string | null;
  selectedTimeRange: "7d" | "30d" | "90d" | "1y";
  selectedCourse: string | null;
  selectedMetric: "engagement" | "progress" | "performance" | "content";
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeType?: "positive" | "negative" | "neutral";
  icon: React.ReactNode;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeType = "neutral",
  icon,
  description,
}) => {
  const getChangeIcon = () => {
    if (changeType === "positive") return <ArrowUp className="w-3 h-3" />;
    if (changeType === "negative") return <ArrowDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  const getChangeColor = () => {
    if (changeType === "positive") return "text-green-600";
    if (changeType === "negative") return "text-red-600";
    return "text-gray-600";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">{icon}</div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">{title}</h3>
            <div className="text-2xl font-bold text-gray-900">{value}</div>
          </div>
        </div>

        {change !== undefined && (
          <div className={`flex items-center space-x-1 ${getChangeColor()}`}>
            {getChangeIcon()}
            <span className="text-sm font-medium">{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {description && (
        <p className="mt-2 text-sm text-gray-600">{description}</p>
      )}
    </div>
  );
};

interface EngagementChartProps {
  data: StudentEngagement[];
  timeRange: string;
}

const EngagementChart: React.FC<EngagementChartProps> = ({
  data,
  timeRange,
}) => {
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    activeUsers: item.activeUsers || 0,
    avgSessionDuration: item.avgSessionDuration || 0,
    totalInteractions: item.totalInteractions || 0,
  }));

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Student Engagement
          </h3>
          <div className="text-sm text-gray-500">Last {timeRange}</div>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No engagement data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Student Engagement
        </h3>
        <div className="text-sm text-gray-500">Last {timeRange}</div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area
            type="monotone"
            dataKey="activeUsers"
            stackId="1"
            stroke="#3B82F6"
            fill="#3B82F6"
            fillOpacity={0.6}
            name="Active Users"
          />
          <Area
            type="monotone"
            dataKey="totalInteractions"
            stackId="2"
            stroke="#10B981"
            fill="#10B981"
            fillOpacity={0.6}
            name="Interactions"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

interface ProgressChartProps {
  data: CourseProgress[];
}

const ProgressChart: React.FC<ProgressChartProps> = ({ data }) => {
  const chartData = data.map((course) => ({
    name: course.courseName || "Unknown Course",
    completed: course.completionRate || 0,
    inProgress: 100 - (course.completionRate || 0),
    avgScore: course.averageScore || 0,
    students: course.enrolledStudents || 0,
  }));

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Course Progress
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No progress data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Course Progress
      </h3>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} />
          <YAxis dataKey="name" type="category" width={120} />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="completed"
            stackId="progress"
            fill="#10B981"
            name="Completed %"
          />
          <Bar
            dataKey="inProgress"
            stackId="progress"
            fill="#E5E7EB"
            name="Remaining %"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface AssessmentPerformanceProps {
  analytics: AssessmentAnalytics;
}

const AssessmentPerformance: React.FC<AssessmentPerformanceProps> = ({
  analytics,
}) => {
  if (!analytics || !analytics.gradeDistribution) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Assessment Performance
          </h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            No assessment data available
          </div>
        </div>
      </div>
    );
  }

  const gradeDistribution = [
    {
      name: "A (70-100%)",
      value: analytics.gradeDistribution?.A || 0,
      color: "#10B981",
    },
    {
      name: "B (60-69.99%)",
      value: analytics.gradeDistribution?.B || 0,
      color: "#3B82F6",
    },
    {
      name: "C (50-59.99%)",
      value: analytics.gradeDistribution?.C || 0,
      color: "#F59E0B",
    },
    {
      name: "D (40-49.99%)",
      value: analytics.gradeDistribution?.D || 0,
      color: "#F97316",
    },
    {
      name: "F (0-39.99%)",
      value: analytics.gradeDistribution?.F || 0,
      color: "#EF4444",
    },
  ];

  const performanceTrends = (analytics.performanceTrends || []).map(
    (item: any) => ({
      date: new Date(item.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      avgScore: item.averageScore || 0,
      completionRate: item.completionRate || 0,
    })
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Grade Distribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Grade Distribution
        </h3>

        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={gradeDistribution}
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) =>
                `${name}: ${(percent * 100).toFixed(0)}%`
              }
            >
              {gradeDistribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>

        <div className="mt-4 space-y-2">
          {gradeDistribution.map((grade, index) => (
            <div
              key={index}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: grade.color }}
                />
                <span className="text-gray-700">{grade.name}</span>
              </div>
              <span className="font-medium">{grade.value} students</span>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Trends */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Performance Trends
        </h3>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={performanceTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="avgScore"
              stroke="#3B82F6"
              strokeWidth={2}
              name="Average Score"
            />
            <Line
              type="monotone"
              dataKey="completionRate"
              stroke="#10B981"
              strokeWidth={2}
              name="Completion Rate"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

interface ContentAnalyticsProps {
  analytics: ContentAnalytics;
}

const ContentAnalyticsComponent: React.FC<ContentAnalyticsProps> = ({
  analytics,
}) => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();

  const contentEngagement = (analytics.topContent || []).map((item: any) => ({
    title:
      item.title.length > 30 ? item.title.substring(0, 30) + "..." : item.title,
    views: item.views,
    avgRating: item.averageRating,
    completionRate: item.completionRate,
    type: item.type,
  }));

  const getContentIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4 text-blue-600" />;
      case "document":
        return <FileText className="w-4 h-4 text-green-600" />;
      case "quiz":
        return <Target className="w-4 h-4 text-purple-600" />;
      default:
        return <BookOpen className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Content Performance Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top Content Performance
        </h3>

        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={contentEngagement} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="title" type="category" width={150} />
            <Tooltip />
            <Legend />
            <Bar dataKey="views" fill="#3B82F6" name="Views" />
            <Bar
              dataKey="completionRate"
              fill="#10B981"
              name="Completion Rate %"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Content Details Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Content Details
          </h3>
        </div>

        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completion Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Time Spent
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(analytics.topContent || []).map(
                (content: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getContentIcon(content.type)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {content.title}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          content.type === "video"
                            ? "bg-blue-100 text-blue-800"
                            : content.type === "document"
                            ? "bg-green-100 text-green-800"
                            : content.type === "quiz"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {content.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {content.views.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 mr-1" />
                        {content.averageRating.toFixed(1)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${content.completionRate}%` }}
                          />
                        </div>
                        {content.completionRate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round(content.averageTimeSpent / 60)} min
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const LearningAnalyticsDashboard: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const tableScrollRef2 = useHorizontalDragScroll<HTMLDivElement>();
  const [state, setState] = useState<AnalyticsState>({
    overview: null,
    studentEngagement: [],
    courseProgress: [],
    assessmentAnalytics: null,
    contentAnalytics: null,
    loading: true,
    error: null,
    selectedTimeRange: "30d",
    selectedCourse: null,
    selectedMetric: "engagement",
  });

  useEffect(() => {
    loadAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedTimeRange, state.selectedCourse]);

  // If a quizId param is present, show quiz-specific analytics
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quizAnalytics, setQuizAnalytics] = useState<any | null>(null);
  const [quizLoading, setQuizLoading] = useState(false);

  // Extract loader so UI can call it (retry button) and so the effect only triggers it
  const loadQuizAnalytics = async (id?: string) => {
    if (!id) return;
    try {
      setQuizLoading(true);
      const data = await quizService.getQuizAnalytics(id);
      setQuizAnalytics(data);
    } catch (err) {
      console.error("Failed to load quiz analytics", err);
      setQuizAnalytics(null);
    } finally {
      setQuizLoading(false);
    }
  };

  useEffect(() => {
    if (quizId) loadQuizAnalytics(quizId);
  }, [quizId]);

  const loadAnalyticsData = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const params = {
        timeRange: state.selectedTimeRange,
        courseId: state.selectedCourse,
      };

      // Call endpoints individually so missing routes don't break the whole page
      let overviewRes: any = null;
      let engagementRes: any = null;
      let progressRes: any = null;
      let assessmentRes: any = null;
      let contentRes: any = null;

      try {
        overviewRes = await apiClient.get("/elearning/analytics/overview", {
          params,
        });
      } catch (e: any) {
        console.debug(
          "overview analytics not available",
          (e && e.message) || e
        );
        overviewRes = null;
      }

      try {
        engagementRes = await apiClient.get("/elearning/analytics/engagement", {
          params,
        });
      } catch (e: any) {
        console.debug(
          "engagement analytics not available",
          (e && e.message) || e
        );
        engagementRes = null;
      }

      try {
        progressRes = await apiClient.get("/elearning/analytics/progress", {
          params,
        });
      } catch (e: any) {
        console.debug(
          "progress analytics not available",
          (e && e.message) || e
        );
        progressRes = null;
      }

      try {
        assessmentRes = await apiClient.get(
          "/elearning/analytics/assessments",
          { params }
        );
      } catch (e: any) {
        console.debug(
          "assessments analytics not available",
          (e && e.message) || e
        );
        assessmentRes = null;
      }

      try {
        contentRes = await apiClient.get("/elearning/analytics/content", {
          params,
        });
      } catch (e: any) {
        console.debug("content analytics not available", (e && e.message) || e);
        contentRes = null;
      }

      // apiClient may unwrap response.data already; normalize to either .data or direct value
      const normalize = (r: any) => (r == null ? null : r.data ?? r);

      setState((prev) => ({
        ...prev,
        overview: normalize(overviewRes),
        studentEngagement: normalize(engagementRes) || [],
        courseProgress: normalize(progressRes) || [],
        assessmentAnalytics: normalize(assessmentRes),
        contentAnalytics: normalize(contentRes),
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load analytics data",
        loading: false,
      }));
    }
  };

  const exportReport = async () => {
    try {
      const response = await apiClient.get("/elearning/analytics/export", {
        params: {
          timeRange: state.selectedTimeRange,
          courseId: state.selectedCourse,
          format: "pdf",
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `analytics-report-${state.selectedTimeRange}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting report:", error);
    }
  };

  // If viewing quiz-specific analytics always render a consistent layout so user sees page chrome
  if (quizId) {
    // prepare distribution safely
    const scoreDistribution = (quizAnalytics &&
      quizAnalytics.scoreDistribution) || {
      "0-20": 0,
      "21-40": 0,
      "41-60": 0,
      "61-80": 0,
      "81-100": 0,
    };

    const distributionData = Object.keys(scoreDistribution).map((k) => ({
      name: k,
      value: (scoreDistribution as any)[k] || 0,
    }));

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {quizAnalytics?.title || "Quiz Analytics"}
                </h1>
                <p className="text-gray-600 mt-1">
                  Basic metrics for this quiz
                </p>
              </div>
              <div className="text-sm text-gray-600">
                Total attempts: {quizAnalytics?.totalAttempts ?? 0}
              </div>
            </div>
          </div>

          {/* If loading show spinner in content area */}
          {quizLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading quiz analytics...</p>
              </div>
            </div>
          )}

          {/* If not loading and no analytics show an informative card with actions */}
          {!quizLoading && !quizAnalytics && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    No analytics available
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Analytics data for this quiz is not available. This may be
                    because no one has attempted the quiz yet or the analytics
                    collection has not been run.
                  </p>
                  <div className="space-x-2">
                    <button
                      onClick={() => loadQuizAnalytics(quizId)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Retry
                    </button>
                    <button
                      onClick={() => navigate("/elearning/quizzes")}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Back to quizzes
                    </button>
                  </div>
                </div>
                <TrendingUp className="w-16 h-16 text-gray-300" />
              </div>
            </div>
          )}

          {/* If analytics exist render the full analytics sections */}
          {!quizLoading && quizAnalytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <MetricCard
                  title="Average Score"
                  value={`${
                    quizAnalytics.averageScore?.toFixed?.(1) ??
                    quizAnalytics.averageScore ??
                    0
                  }%`}
                  icon={<Award className="w-5 h-5 text-blue-600" />}
                />

                <MetricCard
                  title="Pass Rate"
                  value={`${quizAnalytics.passRate ?? 0}%`}
                  icon={<CheckCircle className="w-5 h-5 text-green-600" />}
                />

                <MetricCard
                  title="Avg Time"
                  value={`${Math.round(
                    (quizAnalytics.averageTimeSpent || 0) / 60
                  )}m`}
                  icon={<Clock className="w-5 h-5 text-blue-600" />}
                />
              </div>

              <div className="bg-white rounded-lg border p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Score Distribution
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={distributionData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Recent Attempts
                </h3>
                <div ref={tableScrollRef2} className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Student
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Score
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Time Spent
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(quizAnalytics.recentAttempts || []).map(
                        (a: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {a.studentName || a.student?.name || "Student"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {a.score ?? "-"}%
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {Math.round((a.timeSpent || 0) / 60)}m
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {a.status || "-"}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <TrendingUp className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Analytics
          </h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button
            onClick={loadAnalyticsData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Learning Analytics
              </h1>
              <p className="text-gray-600 mt-1">
                Comprehensive insights into learning performance
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <select
                value={state.selectedTimeRange}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    selectedTimeRange: e.target.value as any,
                  }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>

              <button
                onClick={loadAnalyticsData}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>

              <button
                onClick={exportReport}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Metric Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex space-x-1">
            {[
              {
                key: "engagement",
                label: "Engagement",
                icon: <Users className="w-4 h-4" />,
              },
              {
                key: "progress",
                label: "Progress",
                icon: <TrendingUp className="w-4 h-4" />,
              },
              {
                key: "performance",
                label: "Performance",
                icon: <Award className="w-4 h-4" />,
              },
              {
                key: "content",
                label: "Content",
                icon: <BookOpen className="w-4 h-4" />,
              },
            ].map((metric) => (
              <button
                key={metric.key}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    selectedMetric: metric.key as any,
                  }))
                }
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  state.selectedMetric === metric.key
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {metric.icon}
                <span>{metric.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Overview Metrics */}
        {state.overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <MetricCard
              title="Total Students"
              value={(state.overview as any).totalStudents.toLocaleString()}
              change={(state.overview as any).studentGrowth}
              changeType={
                (state.overview as any).studentGrowth > 0
                  ? "positive"
                  : "negative"
              }
              icon={<Users className="w-5 h-5 text-blue-600" />}
            />

            <MetricCard
              title="Active Courses"
              value={(state.overview as any).activeCourses}
              change={(state.overview as any).courseGrowth}
              changeType={
                (state.overview as any).courseGrowth > 0
                  ? "positive"
                  : "negative"
              }
              icon={<BookOpen className="w-5 h-5 text-blue-600" />}
            />

            <MetricCard
              title="Completion Rate"
              value={`${(state.overview as any).averageCompletionRate}%`}
              change={(state.overview as any).completionRateChange}
              changeType={
                (state.overview as any).completionRateChange > 0
                  ? "positive"
                  : "negative"
              }
              icon={<CheckCircle className="w-5 h-5 text-blue-600" />}
            />

            <MetricCard
              title="Avg Session Time"
              value={`${Math.round(
                (state.overview as any).averageSessionDuration / 60
              )}m`}
              change={(state.overview as any).sessionDurationChange}
              changeType={
                (state.overview as any).sessionDurationChange > 0
                  ? "positive"
                  : "negative"
              }
              icon={<Clock className="w-5 h-5 text-blue-600" />}
            />
          </div>
        )}

        {/* Metric-specific Content */}
        <div className="space-y-6">
          {state.selectedMetric === "engagement" && (
            <EngagementChart
              data={state.studentEngagement}
              timeRange={state.selectedTimeRange}
            />
          )}

          {state.selectedMetric === "progress" && (
            <ProgressChart data={state.courseProgress} />
          )}

          {state.selectedMetric === "performance" &&
            state.assessmentAnalytics && (
              <AssessmentPerformance analytics={state.assessmentAnalytics} />
            )}

          {state.selectedMetric === "content" && state.contentAnalytics && (
            <ContentAnalyticsComponent analytics={state.contentAnalytics} />
          )}
        </div>

        {/* At-Risk Students Alert */}
        {state.overview && (state.overview as any).atRiskStudents > 0 && (
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900">
                  {(state.overview as any).atRiskStudents} Students Need
                  Attention
                </h3>
                <p className="text-orange-800 mt-1">
                  These students have low engagement or poor performance
                  metrics. Consider reaching out for support.
                </p>
                <button className="mt-3 text-sm bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700">
                  View Details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningAnalyticsDashboard;
